import _ from 'underscore';
import { seriesColor as defaultSeriesColor } from './constants';

function uiNextTooltipPositioner(labelWidth, labelHeight, point) {
  const margin = 20;
  // reposition label so it does not go beyond chart bottom
  // a side effect is by doing this highchart gives up auto-positions x axis as well
  // so a similar calculation is done for x axis as well
  return {
    x: (point.plotX + labelWidth) > (this.chart.plotWidth + margin) ?
      (this.chart.plotWidth + margin) - labelWidth :
      point.plotX,
    y: (point.plotY + labelHeight) > (this.chart.plotHeight + margin) ?
      (this.chart.plotHeight + margin) - labelHeight :
      point.plotY,
  };
}

export class ChartFormatter {
  /* eslint-disable no-param-reassign */
  setSeriesMarker(series, isMovingAverageOn) {
    _.each(series, (sery, index) => {
      let selectedSymbol;
      let selectedDashStyle;
      switch (index) {
        case 0:
          selectedSymbol = 'circle';
          selectedDashStyle = 'solid';
          break;
        // eslint-disable-next-line no-case-declarations
        case 1:
          // when there are 4 series, index 1 is always primary (circle dashed line)
          // either primary comparison or primary moving average
          // when there are only 2 series, index 1 could be either primary or secondary
          // 1. if it's primary comparison or primary moving average, style is circle dashed line
          // 2. if it's secondary, style is diamond solid line
          const isPrimary = isMovingAverageOn || (sery.id === 'primaryComparison');
          selectedSymbol = isPrimary ? 'circle' : 'diamond';
          selectedDashStyle = isPrimary ? 'ShortDash' : 'solid';
          break;
        case 2:
          selectedSymbol = 'diamond';
          selectedDashStyle = 'solid';
          break;
        case 3:
          selectedSymbol = 'diamond';
          selectedDashStyle = 'ShortDash';
          break;
        default:
          break;
      }

      sery.marker = {
        symbol: selectedSymbol,
        enabled: true,
        radius: 6,
        fillColor: sery.color,
        lineColor: '#FFFFFF',
        lineWidth: 2,
      };
      sery.dashStyle = selectedDashStyle;
      sery.lineWidth = 2;
    });
  }

  getRawDate = (date, i18n) => i18n.formatDate(date, { raw: 'MM/dd/yyyy' })

  getFormattedToolTip = (chartAdapter, options, seriesColor) => ({
    formatter() {
      const primaryPoints = [];
      const comparisonPoints = [];
      let tempHtml = '';
      _.each(this.points, (point) => {
        if (!_.isUndefined(point.series.userOptions)) {
          switch (point.series.userOptions.id) {
            case 'primary':
              point.id = options.primaryMetric.id;
              primaryPoints.push(point);
              break;
            case 'secondary':
              point.id = options.secondaryMetric.id;
              primaryPoints.push(point);
              break;
            case 'primaryComparison':
              point.id = options.primaryMetric.id;
              comparisonPoints.push(point);
              break;
            case 'secondaryComparison':
              point.id = options.secondaryMetric.id;
              comparisonPoints.push(point);
              break;
            default:
              break;
          }
        } else {
          // FIXME: using the color of a point is not a reliable way to find out its type.
          switch (point.color) {
            case seriesColor.primary.original:
              point.id = options.primaryMetric.id;
              primaryPoints.push(point);
              break;
            case seriesColor.secondary.original:
              point.id = options.secondaryMetric.id;
              primaryPoints.push(point);
              break;
            case seriesColor.primary.comparative:
              point.id = options.primaryMetric.id;
              comparisonPoints.push(point);
              break;
            case seriesColor.secondary.comparative:
              point.id = options.secondaryMetric.id;
              comparisonPoints.push(point);
              break;
            default:
              break;
          }
        }
      });

      const getToolTipValueHtml = point => `<br/><span class="tool-tip-value" style="color: ${point.color};">${chartAdapter.formatMetric(point.id, point.y)}</span></div>`;

      if (primaryPoints.length > 0) {
        tempHtml = `<div class="charts-tooltip"><span>${primaryPoints[0].key}</span>`;
        tempHtml = `${tempHtml}<br/>`;
        _.each(primaryPoints, (point, i) => {
          const divClass = (i === 0) ? 'primary' : 'primary-comparison';
          tempHtml = `${tempHtml}<br /><div class="${divClass}"><span class="toolTipMetric" >${point.series.name} </span>${getToolTipValueHtml(point)}`;
        });
        tempHtml += '</div>';
      }

      if (comparisonPoints.length > 0) {
        tempHtml = options.isMovingAverageOn ?
          `${tempHtml}<div class="charts-tooltip"><span>Moving average</span>` :
          `${tempHtml}<div class="charts-tooltip"><span><b>${comparisonPoints[0].key}</b></span>`;

        tempHtml = `${tempHtml}<br />`;

        _.each(comparisonPoints, (point, i) => {
          const divClass = (i === 0) ? 'movingaverage' : 'movingaverage-comparison';
          tempHtml = `${tempHtml}<br /><div class="${divClass}"><span class="toolTipMetric" >${point.series.name} </span>${getToolTipValueHtml(point)}`;
        });
      }
      tempHtml = `${tempHtml}</div>`;

      return `<div class="perf-graph-tool-tip pop">${tempHtml}</div>`;
    },
  })

  getFormattedSeries = (id, name, color, chartAdapter, series, granularityId) => {
    const data = _.map(series, entry =>
      [chartAdapter.formatDate(entry[2], granularityId),
        entry[1]]);

    return {
      id,
      name,
      color,
      data,
    };
  }

  getFormattedHighChart = (chartAdapter, options, allData) => {
    const seriesColor = _.isEmpty(options.seriesColors) ? defaultSeriesColor : options.seriesColors;
    const chart = { height: 143, width: options.chartWidth };
    const series = [];
    const title = { text: '' };
    let tooltip = {};

    const primaryMetricName = _.isUndefined(options.primaryMetric) ?
      ''
      : options.primaryMetric.value;
    const secondaryMetricName = _.isUndefined(options.secondaryMetric) ?
      ''
      : options.secondaryMetric.value;

    let seriesIndex = 0;
    const primarySeries = _.isUndefined(options.primaryMetric) ?
      null
      : allData[options.primaryMetric.id];

    if (!(_.isNull(primarySeries) || _.isUndefined(primarySeries))) {
      series[seriesIndex] = this.getFormattedSeries(
        'primary',
        primaryMetricName,
        seriesColor.primary.original,
        chartAdapter,
        primarySeries.PrimaryData,
        options.granularityId
      );

      if (!_.isUndefined(primarySeries.ComparisonData)) {
        seriesIndex += 1;
        series[seriesIndex] = this.getFormattedSeries(
          'primaryComparison',
          primaryMetricName,
          seriesColor.primary.comparative,
          chartAdapter,
          primarySeries.ComparisonData,
          options.granularityId
        );
      }
    }

    const secondarySeries = (_.isUndefined(options.primaryMetric) ||
      (options.secondaryMetric.id === -1)) ?
      null
      : allData[options.secondaryMetric.id];

    if (!(_.isNull(secondarySeries) || _.isUndefined(secondarySeries))) {
      seriesIndex += 1;
      series[seriesIndex] = this.getFormattedSeries(
        'secondary',
        secondaryMetricName,
        seriesColor.secondary.original,
        chartAdapter,
        secondarySeries.PrimaryData,
        options.granularityId
      );
      series[seriesIndex].yAxis = 1;

      if (!_.isUndefined(secondarySeries.ComparisonData)) {
        seriesIndex += 1;
        series[seriesIndex] = this.getFormattedSeries(
          'secondaryComparison',
          secondaryMetricName,
          seriesColor.secondary.comparative,
          chartAdapter,
          secondarySeries.ComparisonData,
          options.granularityId
        );
        series[seriesIndex].yAxis = 1;
      }
    }

    this.setSeriesMarker(series, options.isMovingAverageOn);

    tooltip = this.getFormattedToolTip(chartAdapter, options, seriesColor);

    _.extend(tooltip, {
      positioner: uiNextTooltipPositioner,
    });

    return {
      chart,
      series,
      title,
      tooltip,
    };
  }
}
